import {
  collection,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore';
import moment from 'moment';
import type {
  OrderLogActor,
  OrderLogDeviceInfo,
  OrderLogEntry,
  OrderLogSnapshot,
  OrderLogSummary,
  OrderLogWriteInput,
} from '@src/types/orderLogging';
import { isKotSessionCartKey } from '@utilities/kotSession';

export const ORDER_LOGGING_COLLECTION = 'order-logging';

const createClientRequestId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `ord-log-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const readActorFromStorage = (): OrderLogActor => {
  try {
    const raw = localStorage.getItem('userData');
    if (!raw) return { uid: '', phone: '', name: '' };
    const user = JSON.parse(raw) as Record<string, unknown>;
    return {
      uid: String(user.uid || ''),
      phone: String(user.phoneNumber || user.phone || ''),
      name: String(user.name || ''),
    };
  } catch {
    return { uid: '', phone: '', name: '' };
  }
};

/** Best-effort browser / device fingerprint for audit trails. */
export function collectDeviceInfo(): OrderLogDeviceInfo {
  const nav = typeof navigator !== 'undefined' ? navigator : ({} as Navigator);
  const scr =
    typeof window !== 'undefined' && window.screen
      ? window.screen
      : ({} as Screen);
  const conn =
    (nav as Navigator & { connection?: any; mozConnection?: any; webkitConnection?: any })
      .connection ||
    (nav as any).mozConnection ||
    (nav as any).webkitConnection ||
    null;

  let timezone = '';
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    timezone = '';
  }

  return {
    userAgent: String(nav.userAgent || ''),
    platform: String(nav.platform || ''),
    vendor: String(nav.vendor || ''),
    language: String(nav.language || ''),
    languages: Array.isArray(nav.languages) ? [...nav.languages] : [],
    cookieEnabled: !!nav.cookieEnabled,
    online: typeof nav.onLine === 'boolean' ? nav.onLine : true,
    hardwareConcurrency:
      typeof nav.hardwareConcurrency === 'number' ? nav.hardwareConcurrency : null,
    deviceMemory:
      typeof (nav as any).deviceMemory === 'number' ? (nav as any).deviceMemory : null,
    maxTouchPoints: typeof nav.maxTouchPoints === 'number' ? nav.maxTouchPoints : 0,
    screenWidth: typeof scr.width === 'number' ? scr.width : null,
    screenHeight: typeof scr.height === 'number' ? scr.height : null,
    availWidth: typeof scr.availWidth === 'number' ? scr.availWidth : null,
    availHeight: typeof scr.availHeight === 'number' ? scr.availHeight : null,
    colorDepth: typeof scr.colorDepth === 'number' ? scr.colorDepth : null,
    pixelDepth: typeof scr.pixelDepth === 'number' ? scr.pixelDepth : null,
    viewportWidth: typeof window !== 'undefined' ? window.innerWidth : null,
    viewportHeight: typeof window !== 'undefined' ? window.innerHeight : null,
    devicePixelRatio: typeof window !== 'undefined' ? window.devicePixelRatio : null,
    timezone,
    timezoneOffsetMin: new Date().getTimezoneOffset(),
    connectionType: conn?.type ? String(conn.type) : null,
    connectionEffectiveType: conn?.effectiveType ? String(conn.effectiveType) : null,
    connectionDownlink: typeof conn?.downlink === 'number' ? conn.downlink : null,
    connectionRtt: typeof conn?.rtt === 'number' ? conn.rtt : null,
    isWebView: !!(typeof window !== 'undefined' && (window as any).ReactNativeWebView),
    touchSupport:
      (typeof nav.maxTouchPoints === 'number' && nav.maxTouchPoints > 0) ||
      (typeof window !== 'undefined' && 'ontouchstart' in window),
  };
}

const safeClone = (value: unknown): unknown => {
  try {
    return value === null || value === undefined ? value : JSON.parse(JSON.stringify(value));
  } catch {
    return null;
  }
};

const pickFoodRaw = (order: any): Record<string, unknown> => ({
  uoId: order?.uoId ?? null,
  orderSeqNo: order?.orderSeqNo ?? null,
  tableNumber: order?.tableNumber ?? null,
  serviceType: order?.serviceType ?? null,
  cart: safeClone(order?.cart) ?? null,
  itemsCost: order?.itemsCost ?? null,
  gstBill: order?.gstBill ?? null,
  gstData: safeClone(order?.gstData) ?? null,
  serviceBill: order?.serviceBill ?? null,
  discount: order?.discount ?? null,
  totalCost: order?.totalCost ?? null,
  isGstBill: order?.isGstBill ?? null,
  selectedPayment: order?.selectedPayment ?? null,
  customerName: order?.customerName ?? null,
  customerPhone: order?.customerPhone ?? null,
  createdAt: order?.createdAt ?? null,
  updatedAt: order?.updatedAt ?? null,
});

const pickRoomRaw = (order: any): Record<string, unknown> => ({
  uoId: order?.uoId ?? null,
  orderSeqNo: order?.orderSeqNo ?? null,
  guestName: order?.guestName ?? null,
  guestMobile: order?.guestMobile ?? null,
  guestAddress: order?.guestAddress ?? null,
  guestOther: order?.guestOther ?? null,
  guestExtraInfo: order?.guestExtraInfo ?? null,
  guestCompanyName: order?.guestCompanyName ?? null,
  guestCompanyGST: order?.guestCompanyGST ?? null,
  rooms: safeClone(order?.rooms) ?? null,
  checkInDate: order?.checkInDate ?? null,
  checkOutDate: order?.checkOutDate ?? null,
  discount: order?.discount ?? null,
  advancePayment: order?.advancePayment ?? null,
  selectedPayment: order?.selectedPayment ?? null,
  itemsCost: order?.itemsCost ?? null,
  gstBill: order?.gstBill ?? null,
  gstRate: order?.gstRate ?? null,
  serviceBill: order?.serviceBill ?? null,
  totalCost: order?.totalCost ?? null,
  isGstBill: order?.isGstBill ?? null,
  identificationFiles: safeClone(order?.identificationFiles) ?? null,
  foodBills: safeClone(order?.foodBills) ?? null,
  createdAt: order?.createdAt ?? null,
  updatedAt: order?.updatedAt ?? null,
});

export function buildFoodOrderSnapshot(order: any): OrderLogSnapshot {
  const cart = order?.cart && typeof order.cart === 'object' ? order.cart : {};
  const lines: string[] = [];
  let itemCount = 0;

  Object.entries(cart).forEach(([key, val]: [string, any]) => {
    if (isKotSessionCartKey(key) || !val || typeof val !== 'object') return;
    const qty = Number(val.selected) || 0;
    if (qty <= 0) return;
    itemCount += 1;
    const name = String(val.name || key).replaceAll('-', ' ');
    const variant = String(val.selectedVariants?.label || '').replaceAll('₹', '').trim();
    lines.push(variant ? `${name} (${variant}) x${qty}` : `${name} x${qty}`);
  });

  return {
    uoId: order?.uoId ?? null,
    orderSeqNo: order?.orderSeqNo ?? null,
    tableNumber: order?.tableNumber ?? null,
    serviceType: order?.serviceType ?? null,
    totalCost: order?.totalCost ?? null,
    itemsCost: order?.itemsCost ?? null,
    gstBill: order?.gstBill ?? null,
    serviceBill: order?.serviceBill ?? null,
    discount: order?.discount ?? null,
    isGstBill: order?.isGstBill ?? null,
    selectedPayment: order?.selectedPayment ?? null,
    customerName: order?.customerName ?? null,
    customerPhone: order?.customerPhone ?? null,
    createdAt: order?.createdAt ?? null,
    updatedAt: order?.updatedAt ?? null,
    itemCount,
    cartSummary: lines.slice(0, 40).join('; '),
    raw: pickFoodRaw(order),
  };
}

export function buildRoomBillSnapshot(order: any): OrderLogSnapshot {
  const rooms = Array.isArray(order?.rooms) ? order.rooms : [];
  const roomNumbers = rooms
    .map((r: any) => r?.roomNumber || r?.roomType || '')
    .filter(Boolean)
    .map(String);

  return {
    uoId: order?.uoId ?? null,
    orderSeqNo: order?.orderSeqNo ?? null,
    guestName: order?.guestName ?? null,
    guestMobile: order?.guestMobile ?? null,
    totalCost: order?.totalCost ?? null,
    itemsCost: order?.itemsCost ?? null,
    gstBill: order?.gstBill ?? null,
    serviceBill: order?.serviceBill ?? null,
    discount: order?.discount ?? null,
    isGstBill: order?.isGstBill ?? null,
    selectedPayment: order?.selectedPayment ?? null,
    createdAt: order?.createdAt ?? null,
    updatedAt: order?.updatedAt ?? null,
    checkInDate: order?.checkInDate ?? null,
    checkOutDate: order?.checkOutDate ?? null,
    roomCount: rooms.length,
    roomNumbers,
    cartSummary: roomNumbers.join(', '),
    raw: pickRoomRaw(order),
  };
}

const mapDocToEntry = (id: string, data: Record<string, any>): OrderLogEntry => ({
  id,
  restaurantId: String(data.restaurantId || ''),
  orderId: String(data.orderId || ''),
  collectionName: data.collectionName === 'room-bills' ? 'room-bills' : 'orders',
  orderKind: data.orderKind === 'room' ? 'room' : 'food',
  action: data.action === 'update' ? 'update' : 'delete',
  status: data.status === 'failed' ? 'failed' : 'success',
  source: data.source || 'unknown',
  errorMessage: data.errorMessage ?? null,
  snapshot: data.snapshot && typeof data.snapshot === 'object' ? data.snapshot : {},
  previous: data.previous && typeof data.previous === 'object' ? data.previous : null,
  next: data.next && typeof data.next === 'object' ? data.next : null,
  deletedBy: data.deletedBy || { uid: '', phone: '', name: '' },
  device: data.device || collectDeviceInfo(),
  localDate: String(data.localDate || ''),
  deletedAtMs: Number(data.deletedAtMs) || 0,
  clientRequestId: String(data.clientRequestId || id),
  createdAt: data.createdAt,
  deletedAt: data.deletedAt,
});

/**
 * Append-only order audit log. Never throws — must not affect delete/edit UX.
 */
export async function logOrderEvent(input: OrderLogWriteInput): Promise<string | null> {
  try {
    if (!input?.restaurantId || !input?.orderId) {
      console.warn('[order-logging] skipped: missing restaurantId/orderId');
      return null;
    }

    const action = input.action || 'delete';
    const clientRequestId = input.clientRequestId || createClientRequestId();
    const deletedAtMs = input.deletedAtMs || Date.now();
    const localDate = input.localDate || moment(deletedAtMs).local().format('YYYY-MM-DD');
    const deletedBy: OrderLogActor = {
      ...readActorFromStorage(),
      ...(input.deletedBy || {}),
    };

    const previous = input.previous ?? null;
    const next = input.next ?? null;
    const snapshot =
      input.snapshot ||
      (action === 'update' ? next || previous || {} : input.snapshot || {});

    const payload = {
      restaurantId: input.restaurantId,
      orderId: String(input.orderId),
      collectionName: input.collectionName,
      orderKind: input.orderKind,
      action,
      status: input.status,
      source: input.source || 'unknown',
      errorMessage: input.errorMessage || null,
      snapshot: snapshot || {},
      previous,
      next,
      deletedBy,
      device: collectDeviceInfo(),
      localDate,
      deletedAtMs,
      clientRequestId,
      deletedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
    };

    const db = getFirestore();
    const ref = doc(db, ORDER_LOGGING_COLLECTION, clientRequestId);
    const existing = await getDoc(ref);
    if (existing.exists()) {
      return existing.id;
    }

    await setDoc(ref, payload);
    return clientRequestId;
  } catch (error) {
    console.error('[order-logging] failed to write log (order flow unaffected):', error);
    return null;
  }
}

/** Fire-and-forget — never block POS / delete / edit handlers. */
export function logOrderEventAsync(input: OrderLogWriteInput): void {
  void logOrderEvent(input);
}

export function buildOrderLogSummary(entries: OrderLogEntry[]): OrderLogSummary {
  const summary: OrderLogSummary = {
    total: entries.length,
    successCount: 0,
    failedCount: 0,
    foodCount: 0,
    roomCount: 0,
    deleteCount: 0,
    updateCount: 0,
    bySource: {},
    byUser: {},
  };

  entries.forEach((entry) => {
    if (entry.status === 'failed') summary.failedCount += 1;
    else summary.successCount += 1;

    if (entry.orderKind === 'room') summary.roomCount += 1;
    else summary.foodCount += 1;

    if (entry.action === 'update') summary.updateCount += 1;
    else summary.deleteCount += 1;

    const source = entry.source || 'unknown';
    summary.bySource[source] = (summary.bySource[source] || 0) + 1;

    const user =
      entry.deletedBy?.name || entry.deletedBy?.phone || entry.deletedBy?.uid || 'Unknown';
    summary.byUser[user] = (summary.byUser[user] || 0) + 1;
  });

  return summary;
}

export async function getOrderLogsForDateRange(
  restaurantId: string,
  startMs: number,
  endMs: number
): Promise<OrderLogEntry[]> {
  if (!restaurantId || !startMs || !endMs) return [];

  const db = getFirestore();
  const q = query(
    collection(db, ORDER_LOGGING_COLLECTION),
    where('restaurantId', '==', restaurantId),
    where('deletedAtMs', '>=', startMs),
    where('deletedAtMs', '<=', endMs),
    orderBy('deletedAtMs', 'desc')
  );

  const snap = await getDocs(q);
  return snap.docs.map((d) => mapDocToEntry(d.id, d.data()));
}

export function sortOrderLogsNewestFirst(entries: OrderLogEntry[]): OrderLogEntry[] {
  return [...entries].sort((a, b) => (b.deletedAtMs || 0) - (a.deletedAtMs || 0));
}
